import {Config, LinkedAccount} from '../context/ApplicationContext';
import {AxiosRequestConfig} from 'axios';
import {api, CsrfHeaderAndCookieName, getCsrfTokenValue} from './httpClient';
import config from 'src/config';
import {
    ApiApplyFailure,
    ApiEligibilityFailure,
    EligibilityCheckResponse,
    getEligibilityFailureScenarios, GetFeaturesResponse,
    IBaseResponse,
    linkedTollAccountsApiErrors,
    LinkedTollAccountsResponse,
    mapApplyReasonToScenario,
    OtherError,
    RegistrationResponse,
} from './api-types';
import {Promise} from 'es6-promise';
import {RegistrationForm} from '../types/GaasAppState';
import {isErrorRedirect} from './httpClient/responseInterceptor';

interface GetConfigsResponse extends IBaseResponse {
    configs: Config[]
}

const defaultHeaders: AxiosRequestConfig = {
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    }
};

const headersWithCsrf = (): AxiosRequestConfig => ({
    headers: {
        ...defaultHeaders.headers,
        [CsrfHeaderAndCookieName]: getCsrfTokenValue(),
    },
});

/*
  try api call and redirect if error responses
 */
export const tryApiCall = async (apiCallToTry: () => Promise<any>): Promise<any> => {
    try {
        return await apiCallToTry();
    } catch (err: any) {
        if(isErrorRedirect(err)) {
            document.location.href = `${process.env.REACT_APP_PUBLIC_URL}${err.redirect}`;
        } else {
            // give up
            document.location.href = `${process.env.REACT_APP_PUBLIC_URL}/error/500`;
        }
        return [];
    }
}

export const getConfigsApi = async () => {
    return tryApiCall(async () => {
        const trcConfigsUrl = getTollReliefClaimUrl('/custom/configs');
        const response = await api.get(trcConfigsUrl, defaultHeaders);

        return (response.data as GetConfigsResponse).configs;
    });
};

export const unlinkAccount = async (account: LinkedAccount) => {
    return tryApiCall(async () => {
        const trcConfigsUrl = getTollReliefRegistrationUrl('/custom/unlink-toll-account');
        const request = {
            participantToken: account.participantToken
        };
        const response = await api.post(trcConfigsUrl, request, headersWithCsrf());

        return (response.data as IBaseResponse).status;
    });
}

export const linkedTollAccountsApi = async (): Promise<LinkedAccount[] | linkedTollAccountsApiErrors> => {
    const url = getTollReliefRegistrationUrl('/custom/linked-toll-accounts');

    return tryApiCall(async () => {

        const axiosResponse = await api.get(url, defaultHeaders);
        const responseBody: LinkedTollAccountsResponse = axiosResponse.data;

        if (responseBody.status.code === 'SUCCESS') {
            return responseBody.linkedTollAccounts;
        }
        return responseBody.status.message === 'userHasOtherMyAccount'
            ? 'userHasOtherMyAccount'
            : 'generic'
    });
}

/** For eligibility failure scenarios relating to the toll account */
export enum TollAccountFailure {
    tollAccountDetailsInvalid = 'tollAccountDetailsInvalid',
    tollAccountNotAssociatedToABN = 'tollAccountNotAssociatedToABN',
    tspApiTollAccountNotFound = 'tspApiTollAccountNotFound', // toll account not found when checked via tsp api
    tomeeTollAccountNotFound = 'tomeeTollAccountNotFound', // toll account not found when checked via tomee
    customerHasTollAccountLinkedToAnotherMyAccountId = 'customerHasTollAccountLinkedToAnotherMyAccountId',
    tollAccountInactive = 'tollAccountInactive',
    tollAccountAlreadyLinked = 'tollAccountAlreadyLinked',
    tollAccountAssociatedToABN = 'tollAccountAssociatedToABN',
    myAccountOutsideNSW = 'myAccountOutsideNSW',
    tollAccountOutsideNSW = 'tollAccountOutsideNSW',
    tollAccountCannotBeVerified='tollAccountCannotBeVerified',
    serviceProviderError='serviceProviderError',
    etollApiNotAvailable='etollApiNotAvailable',
    linktApiNotAvailable='linktApiNotAvailable',
    tollAccountProductTypeInvalid='tollAccountProductTypeInvalid',
}

/** For eligibility failure scenarios relating to the sole trader business details. */
export enum SoleTraderFailure {
    abnInactive = 'abnInactive',
    unauthorised = 'unauthorised',
    anzsicViolation = 'anzsicViolation',
    abnAlreadyLinked = 'abnAlreadyLinked',
    abnSuppressed = 'abnSuppressed',
    businessNotInNSW = 'businessNotInNSW',
    abnNotSoleTrader = 'abnNotSoleTrader',
}

/** All eligibility scenarios we can action */
export type EligibilityFailureScenario = TollAccountFailure | SoleTraderFailure | 'other';

/**
 * Calls eligibility, returning failures, or empty list if none.
 */
export const eligibilityApi = async (trRegistrationForm: RegistrationForm): Promise<EligibilityFailureScenario[]> => {
    return tryApiCall(async () => {
        const url = getTollReliefRegistrationUrl('/eligibility/check');

        const axiosResponse = await api.post(url, trRegistrationForm, headersWithCsrf());
        const responseBody: EligibilityCheckResponse = axiosResponse.data;
        const apiFailures = mapEligibilityToFailedClaims(responseBody);
        const failureScenarios = getEligibilityFailureScenarios(apiFailures)
        return Array.from(failureScenarios);
    });
};

/**
 * From a list of eligibility failures,
 * returning only those which we care about on the Sole Trader Toll Account Details page.
 */
export function getTollAccountFailures(failures: EligibilityFailureScenario[]): TollAccountFailure[] {
    const onlyTollAccountFailures = failures.filter((failure) => {
        return failure in TollAccountFailure;
    });
    return onlyTollAccountFailures as TollAccountFailure[];
}

/**
 * From a list of eligibility failures,
 * returning only those which we care about on the Sole Trader Business Details page.
 */
export function getSoleTraderBusinessDetailsFailures(failures: EligibilityFailureScenario[]): SoleTraderFailure[] {
    const onlyBusinessDetailsFailures = failures.filter((failure) => {
        return failure in SoleTraderFailure;
    });
    return onlyBusinessDetailsFailures as SoleTraderFailure[];
}

/**
 * Submits the registration, by calling the /apply endpoint.
 * @return a failure scenario, or null if there were no failures.
 */
export const saveRegistrationApi = async (trRegistrationForm: RegistrationForm): Promise<EligibilityFailureScenario | null> => {
    return tryApiCall(async () => {
        const url = getTollReliefRegistrationUrl('/apply');

        const defaultOptions = headersWithCsrf();
        const options = {
            ...defaultOptions,
            headers: {
                ...defaultOptions.headers,
                // Require submission to run synchronously up to a certain point.
                // If user removes this, they just get a bad experience, but same checks get performed in backend.
                'x-sync-submission': true,
                'x-sync-active': true,
            },
        };

        const response = await api.post(url, trRegistrationForm, options);
        const responseBody: RegistrationResponse = response.data;
        const potentialErrorCode = responseBody.status.reasons?.at(0)?.code as ApiApplyFailure | undefined;

        if (potentialErrorCode === undefined) {
            return null; // success - no failure to return
        }
        if (potentialErrorCode === Object.getOwnPropertyNames(OtherError).find(error => error.includes(potentialErrorCode))) {
            console.error('OtherError occurred');
            throw Error(potentialErrorCode);
        }

        return mapApplyReasonToScenario(potentialErrorCode);
    });
}

export const featureApi: () => Promise<GetFeaturesResponse> = async () => {
    const url = getTollReliefRegistrationUrl('/custom/features');
    const axiosResponse = await api.get(url, headersWithCsrf());
    const responseBody: GetFeaturesResponse = axiosResponse.data;

    return responseBody;
}

/** Grabs all failed eligibility claims */
const mapEligibilityToFailedClaims = (responseBody: EligibilityCheckResponse): ApiEligibilityFailure[] => {
    if (responseBody.claimResult === 'PASS') {
        return [];
    }

    const failedClaims = responseBody.claims?.filter((claim) => {
        return claim.claimResult === 'FAIL'; // Get failed claims
    }).map((failedClaim) => {
        return failedClaim.reasons?.at(0)?.code; // Get the claim codes (assuming not undefined)
    });
    return (failedClaims ?? []) as ApiEligibilityFailure[];
}

const getTollReliefRegistrationUrl = (path: string) => `${config.apiUrl}/${process.env.REACT_APP_TOLLRELIEF_GAAS_API_VERSION}/${config.productCode}${path}`;

const getTollReliefClaimUrl = (path: string) => `${config.apiUrl}/${process.env.REACT_APP_TOLLRELIEF_GAAS_API_VERSION}/TollReliefClaim${path}`;
